import { IBooking } from '../interfaces/heavyDutyMachinery/IBooking';

/**
 * What a booking with no machine in it needs, and from whom.
 *
 * A standalone service booking runs on the same `Booking` row as a machinery
 * rental and reaches the same two screens, but almost none of the machinery
 * milestones mean anything on it: there is no albarán, no check-in report, no
 * machine to hand over. What it has instead is a card saved before anybody
 * travels and a professional declaring the work done -- and those two are what
 * these predicates are for.
 *
 * They live here rather than inline because both sides ask about the same
 * booking from opposite screens (MyBookings for the customer, IncomingBookings
 * for the professional), and a rule that disagreed between the two would show
 * one of them a button the other's server call then refuses.
 */

/**
 * No machine at all. `heavyDutyMachineryId` comes over as an empty string
 * rather than null for these (see BookingResponse's own mapping), which is why
 * this is a falsy test and not a null check.
 */
export const isServiceOnlyBooking = (booking: IBooking): boolean => !booking.heavyDutyMachineryId;

/**
 * Whether the customer still has to leave a card.
 *
 * `hasSavedCard` is the whole reason this is answerable: a held booking spends
 * days in Scheduled both before and after the card is saved, so asking only
 * about the status would keep demanding a card that is already on file -- and
 * the server would answer the button with an error.
 */
export const needsCardOnFile = (booking: IBooking): boolean =>
  isServiceOnlyBooking(booking) &&
  booking.captureMode === 'OnHandover' &&
  !booking.hasSavedCard &&
  (booking.status === 'Pending' || booking.status === 'Confirmed');

/**
 * Whether the professional can declare this job done -- the mirror of
 * BookingController.MarkServiceCompleted's own three walls.
 *
 * Not conditional on the capture mode, deliberately: on a booking paid up front
 * there is nothing left to charge, but the milestone still opens the escrow and
 * releases the money to them within the day instead of at the backstop.
 */
export const canMarkServiceCompleted = (booking: IBooking): boolean =>
  isServiceOnlyBooking(booking) && booking.status === 'Confirmed' && !booking.serviceCompletedAt;

/**
 * Whether the money behind this service booking is frozen rather than taken.
 *
 * What it is for: telling the customer that the charge their bank is showing
 * them as pending is not a charge. Left unsaid, the first thing they do is write
 * asking why they have been billed before anybody turned up.
 */
export const isServiceHoldPlaced = (booking: IBooking): boolean =>
  isServiceOnlyBooking(booking) && booking.paymentStatus === 'Authorized' && !booking.serviceCompletedAt;
